import { RefObject, useEffect, useEffectEvent, useRef } from "react";

import { ScrollbarDraggingData } from "./horizontal_scrollbar.types";
import { calculateForcedScrollableContainerDimensions } from "./utils/calculate_forced_scrollable_container_dimensions";
import { calculateKeyboardScrollableContainerDimensions } from "./utils/calculate_keyboard_scrollable_container_dimensions";
import { calculateScrollableContainerDimensions } from "./utils/calculate_scrollable_container_dimensions";
import { calculateScrollbarDimensions } from "./utils/calculate_scrollbar_dimensions";

type UseHorizontalScrollbarProps = {
  scrollableContainerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  getContentWidth: () => number;
  getWindowWidth: () => number;
  minThumbWidth?: number;
  isAlwaysVisible?: boolean;
  keyboardStep?: number;
};

type UseHorizontalScrollbarReturn = {
  containerProps: {
    ref: RefObject<HTMLDivElement | null>;
  };
  trackProps: {
    onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  };
  thumbProps: {
    "ref": RefObject<HTMLDivElement | null>;
    "role": string;
    "aria-controls": string | undefined;
    "aria-orientation": "horizontal";
    "aria-valuenow": number;
    "aria-valuemin": number;
    "aria-valuemax": number;
    "tabIndex": number;
    "onMouseDown": (e: React.MouseEvent<HTMLDivElement>) => void;
    "onKeyDown": (e: React.KeyboardEvent<HTMLDivElement>) => void;
  };
};

/**
 * Hook that provides horizontal scrollbar functionality
 * Manages:
 * - ResizeObserver to update scrollbar on size changes
 * - Scroll event listener to sync scrollbar position
 * - Mouse events for dragging and track clicking
 * - Keyboard events for accessibility
 * - Aria props for accessibility
 */
export const useHorizontalScrollbar = ({
  scrollableContainerRef,
  contentRef,
  getContentWidth,
  getWindowWidth,
  minThumbWidth = 50,
  isAlwaysVisible,
  keyboardStep
}: UseHorizontalScrollbarProps): UseHorizontalScrollbarReturn => {
  const scrollbarContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollbarRef = useRef<HTMLDivElement | null>(null);
  const scrollbarDraggingData = useRef<ScrollbarDraggingData | null>(null);

  const getContentWidthEvent = useEffectEvent(getContentWidth);
  const getWindowWidthEvent = useEffectEvent(getWindowWidth);

  useEffect(() => {
    const scrollableContainer = scrollableContainerRef.current;
    if (!scrollbarRef.current || !scrollableContainer) {
      return;
    }
    if (!scrollableContainer.id) {
      console.error("scrollableContainer must have an id for accessibility!");
      return;
    }
    scrollbarRef.current.setAttribute("aria-controls", `${scrollableContainer.id}`);
  }, [scrollableContainerRef]);

  useEffect(() => {
    const scrollableContainer = scrollableContainerRef.current;
    if (
      !contentRef.current ||
      !scrollbarContainerRef.current ||
      !scrollbarRef.current ||
      !scrollableContainer
    ) {
      return;
    }

    const contentElement = contentRef.current;
    const scrollbarContainerElement = scrollbarContainerRef.current;

    const recalculate = () => {
      if (!scrollbarRef.current || !scrollbarContainerRef.current) {
        return;
      }
      const { left, width } = calculateScrollbarDimensions({
        contentRef,
        scrollableContainer,
        scrollbarContainerRef,
        scrollbarRef,
        getContentWidth: getContentWidthEvent,
        getWindowWidth: getWindowWidthEvent,
        minThumbWidth
      });
      scrollbarRef.current.style.setProperty("left", `${left}px`);
      scrollbarRef.current.style.setProperty("width", `${width}px`);
      if (!isAlwaysVisible) {
        if (scrollableContainer.clientWidth === scrollableContainer.scrollWidth) {
          scrollbarContainerRef.current.style.setProperty("display", "none");
        } else {
          scrollbarContainerRef.current.style.setProperty("display", "flex");
        }
      }
    };

    // Catches the content element and the scrollbar's own track resizing (e.g. window resize, a
    // movable divider being dragged) - cases where the scrollable container's box size itself changes.
    const resizeObserver = new ResizeObserver(recalculate);
    resizeObserver.observe(contentElement);
    resizeObserver.observe(scrollbarContainerElement);

    // Catches content growing `scrollWidth` without resizing its own box (e.g. async-loaded cells when
    // `contentRef` is the clipping scrollable container itself) - something ResizeObserver can't see.
    let mutationFrame: number | null = null;
    const mutationObserver = new MutationObserver(() => {
      if (mutationFrame !== null) {
        return;
      }
      mutationFrame = requestAnimationFrame(() => {
        mutationFrame = null;
        recalculate();
      });
    });
    mutationObserver.observe(scrollableContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
      characterData: true
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (mutationFrame !== null) {
        cancelAnimationFrame(mutationFrame);
      }
    };
  }, [contentRef, scrollableContainerRef, minThumbWidth, isAlwaysVisible]);

  useEffect(() => {
    const scrollableContainer = scrollableContainerRef.current;
    if (!scrollableContainer) {
      return;
    }

    const abortController = new AbortController();

    const handleScroll = () => {
      if (abortController.signal.aborted) {
        return;
      }

      if (!scrollbarRef.current) {
        return;
      }
      const { left, percentage } = calculateScrollbarDimensions({
        contentRef,
        scrollableContainer,
        scrollbarContainerRef,
        scrollbarRef,
        getContentWidth: getContentWidthEvent,
        getWindowWidth: getWindowWidthEvent,
        minThumbWidth
      });
      scrollbarRef.current.style.setProperty("left", `${left}px`);
      scrollbarRef.current.setAttribute("aria-valuenow", `${percentage}`);
    };

    scrollableContainer.addEventListener("scroll", handleScroll, {
      signal: abortController.signal
    });

    return () => {
      abortController.abort();
    };
  }, [contentRef, scrollableContainerRef, minThumbWidth]);

  const handleThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const scrollableContainer = scrollableContainerRef.current;
    if (!scrollableContainer) {
      return;
    }

    const abortController = new AbortController();

    const handleMouseMove = (e: MouseEvent) => {
      if (abortController.signal.aborted) {
        return;
      }
      const { scrollLeft } = calculateScrollableContainerDimensions(e.clientX, {
        contentRef,
        scrollableContainer,
        scrollbarContainerRef,
        scrollbarRef,
        scrollbarDraggingData
      });
      scrollableContainer.scrollLeft = scrollLeft;
    };

    const handleMouseUp = () => {
      abortController.abort();
      scrollbarDraggingData.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove, { signal: abortController.signal });
    document.addEventListener("mouseup", handleMouseUp, { signal: abortController.signal });

    scrollbarDraggingData.current = {
      mouseX: e.clientX,
      scrollLeft: scrollableContainer.scrollLeft
    };
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const scrollableContainer = scrollableContainerRef.current;
    if (
      !scrollbarContainerRef.current ||
      !scrollableContainer ||
      !scrollbarRef.current ||
      !contentRef.current
    ) {
      return;
    }

    const { scrollLeft } = calculateForcedScrollableContainerDimensions(e.clientX, {
      contentRef,
      scrollableContainer,
      scrollbarContainerRef,
      scrollbarRef
    });

    scrollableContainer.scrollLeft = scrollLeft;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const scrollableContainer = scrollableContainerRef.current;
    if (!scrollableContainer) {
      return;
    }
    const { scrollLeft } = calculateKeyboardScrollableContainerDimensions(
      event,
      scrollableContainer,
      keyboardStep
    );

    scrollableContainer.scrollLeft = scrollLeft;
  };

  return {
    containerProps: {
      ref: scrollbarContainerRef
    },
    trackProps: {
      onMouseDown: handleTrackClick
    },
    thumbProps: {
      "ref": scrollbarRef,
      "role": "scrollbar",
      "aria-controls": undefined,
      "aria-orientation": "horizontal",
      "aria-valuenow": 0,
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "tabIndex": 0,
      "onMouseDown": handleThumbMouseDown,
      "onKeyDown": handleKeyDown
    }
  };
};
